import { Skeleton } from "@mui/material";
import { useAtomValue } from "jotai";
import { type FC, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CeDropdown } from "../generic/components/form-fields/ce-dropdown";
import { multiformatAtom } from "../multiformat/store/multiformat-atom";
import type { CreateContentRequest } from "../multiformat/store/types";
import { useBrandToneOptions } from "./hooks/use-brand-tone-options";

export const ContentToneSelect: FC = () => {
  const { loading, error, data: brandToneOptions } = useBrandToneOptions();
  const { t } = useTranslation();
  const multiformatLoader = useAtomValue(multiformatAtom);
  const previousContentTypeRef = useRef<string | null>(null);

  const { watch, setValue } = useFormContext<CreateContentRequest>();

  const tone = watch("tone");
  const format = watch("format");

  useEffect(
    function handleToneBasedOnContentType() {
      if (!multiformatLoader.data || brandToneOptions?.length === 0) return;

      const selectedContentType = multiformatLoader.data.content_types.find(
        (c) => c.name === format,
      );
      const currentContentType = selectedContentType?.type;
      if (currentContentType === "Communication") {
        // Only set default tone for Communication type if no tone is currently selected
        if (tone === undefined) {
          setValue("tone", brandToneOptions?.find((x) => x.default)?.value);
        }
      } else {
        // Reset tone only when switching FROM communication TO non-communication type
        if (tone !== undefined && previousContentTypeRef.current === "Communication") {
          setValue("tone", undefined);
        }
      }
      // Update the previous content type for next comparison
      previousContentTypeRef.current = currentContentType || null;
    },
    [multiformatLoader.data, brandToneOptions, format, tone, setValue],
  );

  if (loading) {
    <Skeleton height="60px" />;
  }

  if (error) {
    return null;
  }

  return (
    <CeDropdown<CreateContentRequest>
      label={t("Content tone")}
      name="tone"
      loading={loading}
      options={brandToneOptions ?? []}
      InputProps={{
        sx: {
          backgroundColor: "white",
          borderRadius: 2,
        },
      }}
    />
  );
};
